import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import SignalWifi0BarIcon from '@mui/icons-material/SignalWifi0Bar';
import SignalWifi1BarIcon from '@mui/icons-material/SignalWifi1Bar';
import SignalWifi2BarIcon from '@mui/icons-material/SignalWifi2Bar';
import SignalWifi3BarIcon from '@mui/icons-material/SignalWifi3Bar';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalCellular0BarIcon from '@mui/icons-material/SignalCellular0Bar';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import { supportsSignalStrength } from '../../utils/deviceTypes';

const WIFI_SIGNAL_ICONS = [
  SignalWifi0BarIcon,
  SignalWifi1BarIcon,
  SignalWifi2BarIcon,
  SignalWifi3BarIcon,
  SignalWifi4BarIcon,
];

const CELLULAR_SIGNAL_ICONS = [
  SignalCellular0BarIcon,
  SignalCellular1BarIcon,
  SignalCellular2BarIcon,
  SignalCellular3BarIcon,
  SignalCellular4BarIcon,
];

const clampBars = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.max(0, Math.min(5, Math.round(numericValue)));
};

const getSignalColor = (bars) => {
  if (bars === null) {
    return 'var(--mui-palette-action-disabled, #9e9e9e)';
  }

  if (bars <= 1) {
    return 'var(--mui-palette-error-main, #d32f2f)';
  }

  if (bars <= 3) {
    return 'var(--mui-palette-warning-main, #ed6c02)';
  }

  return 'var(--mui-palette-success-main, #2e7d32)';
};

const getSignalIconLevel = (bars) => {
  if (bars === null || bars <= 0) {
    return 0;
  }

  if (bars === 1) {
    return 1;
  }

  if (bars === 2) {
    return 2;
  }

  if (bars === 3) {
    return 3;
  }

  return 4;
};

const normalizeNetworkType = (value) => {
  if (!value) {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (normalized.includes('wifi') || normalized.includes('wi-fi') || normalized.includes('wlan')) {
    return 'wifi';
  }

  if (
    normalized.includes('cell') ||
    normalized.includes('mobile') ||
    normalized.includes('lte') ||
    normalized.includes('4g') ||
    normalized.includes('5g') ||
    normalized.includes('3g')
  ) {
    return 'cellular';
  }

  return null;
};

const getSignalIcon = (bars, networkType) => {
  const normalized = normalizeNetworkType(networkType);
  const iconLevel = getSignalIconLevel(bars);

  if (normalized === 'wifi') {
    return {
      label: 'Wi-Fi',
      icon: WIFI_SIGNAL_ICONS[iconLevel],
    };
  }

  if (normalized === 'cellular') {
    return {
      label: 'Cellular',
      icon: CELLULAR_SIGNAL_ICONS[iconLevel],
    };
  }

  return {
    label: 'Signal',
    icon: CELLULAR_SIGNAL_ICONS[iconLevel],
  };
};

export const SignalStrengthIndicator = ({ value, networkType = null, deviceType = '', showLabel = false }) => {
  if (!supportsSignalStrength(deviceType)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
        Unavailable
      </Typography>
    );
  }

  const bars = clampBars(value);
  const signal = getSignalIcon(bars, networkType);
  const IconComponent = signal.icon;
  const signalLabel = bars === null ? 'Unavailable' : `${bars}/5`;
  const iconColor = getSignalColor(bars);

  return (
    <Tooltip title={`${signal.label} ${signalLabel}`} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: showLabel ? 0.5 : 0,
          color: bars === null ? 'text.disabled' : 'inherit',
          lineHeight: 1,
        }}
      >
        <IconComponent
          sx={{
            fontSize: 20,
            color: iconColor,
          }}
        />
        {showLabel ? (
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1 }}>
            {bars === null ? 'N/A' : `${bars}/5`}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
};

export default SignalStrengthIndicator;